import { useAuth, useUser } from "@clerk/clerk-react"
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "@tanstack/react-query"
import React from "react"
import { useLocation } from "react-router-dom"

import { getOrganizationAccessContext } from "@/api/get-organization-access-context"
import {
  OrganizationAccessContextV1,
  OrganizationAccessContextV1Schema,
} from "@/models/organization-access"
import { ClerkPublicMetadata } from "@/types/clerk-metadata"

interface OrganizationAccessValue {
  context: OrganizationAccessContextV1
  cacheKey: string
  queryKey: (...parts: readonly unknown[]) => readonly unknown[]
}

const OrganizationAccessContext = React.createContext<OrganizationAccessValue | null>(null)

export const organizationAccessQueryKey = (
  clerkUserId: string,
  vividUserId: string,
  organizationId: string,
) => ["organization-access-context", clerkUserId, vividUserId, organizationId] as const

export const createUserBoundQueryClient = (): QueryClient => new QueryClient()

const LoadingScreen = () => (
  <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-[#5D82ED] to-[#C08AF8]">
    <span className="text-sm font-medium text-white">Loading your access…</span>
  </div>
)

const AccessError = ({ retry }: { retry: () => void }) => (
  <div className="flex min-h-screen items-center justify-center bg-zinc-950 px-6 text-zinc-100">
    <div className="max-w-md text-center">
      <h1 className="text-lg font-semibold">We couldn’t load your access</h1>
      <p className="mt-2 text-sm text-zinc-400">
        No organization data has been loaded. Check your connection and try again.
      </p>
      <button
        type="button"
        className="mt-5 rounded-md bg-white px-4 py-2 text-sm font-medium text-zinc-900"
        onClick={retry}
      >
        Try again
      </button>
    </div>
  </div>
)

function QueryClientBoundary({
  children,
  boundaryKey,
}: {
  children: React.ReactNode
  boundaryKey: string
}) {
  const queryClient = React.useMemo(createUserBoundQueryClient, [boundaryKey])
  React.useLayoutEffect(() => () => queryClient.clear(), [queryClient])
  return <QueryClientProvider client={queryClient}>{children}</QueryClientProvider>
}

export function IdentityQueryClientProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const boundaryKey =
    authLoaded && userLoaded
      ? isSignedIn && user
        ? `signed-in:${user.id}`
        : "signed-out"
      : "clerk-loading"

  return (
    <QueryClientBoundary key={boundaryKey} boundaryKey={boundaryKey}>
      {children}
    </QueryClientBoundary>
  )
}

const matchesRoute = (pathname: string, route: string): boolean =>
  pathname === route || pathname.startsWith(`${route}/`)

export const isPublicShareRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/public/form-structure-overview")

export const isSuperAdminRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/super-admin")

export const isAuthenticationRoute = (pathname: string): boolean =>
  matchesRoute(pathname, "/sign-in") || matchesRoute(pathname, "/sign-up")

export const routeBypassesOrganizationAccess = (pathname: string): boolean =>
  isPublicShareRoute(pathname) ||
  isSuperAdminRoute(pathname) ||
  isAuthenticationRoute(pathname) ||
  pathname === "/status"

function PrivateScope({
  children,
  clerkUserId,
  context,
}: {
  children: React.ReactNode
  clerkUserId: string
  context: OrganizationAccessContextV1
}) {
  const cacheKey = `${clerkUserId}:${context.userId}:${context.organizationId}:${context.scopeRevision}`
  const value: OrganizationAccessValue = {
    context,
    cacheKey,
    queryKey: (...parts) => [
      "scoped",
      clerkUserId,
      context.userId,
      context.organizationId,
      context.scopeRevision,
      ...parts,
    ],
  }

  return (
    <QueryClientBoundary boundaryKey={cacheKey}>
      <OrganizationAccessContext.Provider value={value}>
        {children}
      </OrganizationAccessContext.Provider>
    </QueryClientBoundary>
  )
}

function ProtectedOrganizationAccess({
  children,
  clerkUserId,
  vividUserId,
  organizationId,
}: {
  children: React.ReactNode
  clerkUserId: string
  vividUserId: string
  organizationId: string
}) {
  const queryClient = useQueryClient()
  const accessQueryKey = React.useMemo(
    () => organizationAccessQueryKey(clerkUserId, vividUserId, organizationId),
    [clerkUserId, vividUserId, organizationId],
  )
  React.useLayoutEffect(
    () => () => queryClient.removeQueries({ queryKey: accessQueryKey, exact: true }),
    [accessQueryKey, queryClient],
  )
  const accessQuery = useQuery({
    queryKey: accessQueryKey,
    queryFn: async () => {
      const parsed = OrganizationAccessContextV1Schema.parse(
        await getOrganizationAccessContext.query<unknown>(),
      )
      if (parsed.userId !== vividUserId || parsed.organizationId !== organizationId)
        throw new Error("Access context did not match the current identity")
      return parsed
    },
    retry: false,
    gcTime: 0,
    staleTime: 0,
    refetchInterval: 60_000,
    refetchOnMount: "always",
    refetchOnReconnect: "always",
    refetchOnWindowFocus: "always",
  })

  if (accessQuery.isError) return <AccessError retry={() => void accessQuery.refetch()} />
  if (!accessQuery.data) return <LoadingScreen />

  const cacheKey = `${clerkUserId}:${accessQuery.data.userId}:${organizationId}:${accessQuery.data.scopeRevision}`
  return (
    <PrivateScope key={cacheKey} clerkUserId={clerkUserId} context={accessQuery.data}>
      {children}
    </PrivateScope>
  )
}

function ProvisionOrganizationAccess({
  clerkUser,
  children,
}: {
  clerkUser: { id: string; reload: () => Promise<unknown> }
  children: React.ReactNode
}) {
  const provisioningQuery = useQuery({
    queryKey: ["organization-access-provisioning", clerkUser.id],
    queryFn: async () => {
      const context = OrganizationAccessContextV1Schema.parse(
        await getOrganizationAccessContext.query<unknown>(),
      )
      await clerkUser.reload()
      return context
    },
    retry: false,
    gcTime: 0,
  })

  if (provisioningQuery.isError)
    return <AccessError retry={() => void provisioningQuery.refetch()} />
  if (!provisioningQuery.data) return <LoadingScreen />
  return (
    <ProtectedOrganizationAccess
      clerkUserId={clerkUser.id}
      vividUserId={provisioningQuery.data.userId}
      organizationId={provisioningQuery.data.organizationId}
    >
      {children}
    </ProtectedOrganizationAccess>
  )
}

export function OrganizationAccessProvider({ children }: { children: React.ReactNode }) {
  const { isLoaded: authLoaded, isSignedIn } = useAuth()
  const { isLoaded: userLoaded, user } = useUser()
  const { pathname } = useLocation()

  if (!authLoaded || !userLoaded) return <LoadingScreen />
  if (routeBypassesOrganizationAccess(pathname) || !isSignedIn) return <>{children}</>

  const metadata = (user?.publicMetadata || {}) as ClerkPublicMetadata
  const organizationId = metadata.vivid?.organizationId
  const vividUserId = metadata.vivid?.id
  const clerkUserId = user?.id
  if (!user || !clerkUserId) return <AccessError retry={() => window.location.reload()} />
  if (!vividUserId || !organizationId)
    return <ProvisionOrganizationAccess clerkUser={user}>{children}</ProvisionOrganizationAccess>

  return (
    <ProtectedOrganizationAccess
      clerkUserId={clerkUserId}
      vividUserId={vividUserId}
      organizationId={organizationId}
    >
      {children}
    </ProtectedOrganizationAccess>
  )
}

export function useOrganizationAccess(): OrganizationAccessValue {
  const value = React.useContext(OrganizationAccessContext)
  if (!value) throw new Error("Organization access is unavailable")
  return value
}
